import { ref, type Ref } from "vue";
import { v4 as uuid } from "uuid";
import { useTranslation } from "i18next-vue";
import { Facade } from "@/drawing";
import {
  usePlanAnalysisState,
  type AnalysisStep,
} from "@/stores/PlanAnalysisState";
import { useCreateFieldObservation } from "@/stores/queries/field-observations.query";
import {
  groupEntities,
  composeGroup,
  classifyEntity,
  sortAnalysisGroups,
  stableGroupId,
} from "@/utils/plan-analysis/grouping";
import { buildAnalysisDoubts } from "@/utils/plan-analysis/doubts";
import {
  detectVignettes,
  vignetteIdForEntity,
} from "@/utils/plan-analysis/vignettes";
import { bboxDiagonal } from "@/utils/plan-analysis/geometry";
import { buildLegendNoteInfo } from "@/composable/useLegendNoteInfo";
import { drawingRepository } from "@/repositories/drawing.repository";
import { trackTab } from "@/analytics/drawing-analytics";
import APIClient from "@/APIClient";
import { Logger } from "@/logger";
import type { AnalysisScanResult } from "@/drawing/managers/ObservationsManager";
import type {
  AnalysisEntity,
  AnalysisGroup,
  AnalysisGroupKind,
  PhotoAttachment,
  PhotoCandidateFacts,
  PlanAnalysisResult,
  PlanVignette,
  WorldBbox,
} from "@/utils/plan-analysis/types";
import type {
  PlanAnalysisAiRequest,
  PlanAnalysisAiSuggestion,
  PlanEntityForAI,
  PlanGroupingRequest,
  PlanGroupingResponse,
  PlanPhotoFactsForAI,
} from "@/types";

/**
 * Orchestrateur de l'analyse du plan — la seule couche qui touche à la fois
 * au moteur ODA (scan, sélection, stamp) et au store `PlanAnalysisState`.
 *
 * Déroulé : scan XData → vignettes → regroupement local + passe photos (le
 * PRIOR, pas affiché) → deux passes IA pendant l'animation de scan :
 * grouping (l'IA arbitre le découpage des tracés et textes et tranche les
 * photos ambiguës PARMI leurs candidats — proposition locale, faits photo et
 * doutes en entrée) puis describe (l'IA rédige les suggestions —
 * ré-appelable sur un groupe corrigé via `regenerateSuggestion`). Les étapes
 * s'affichent sur le plan au fil de l'eau ; le résultat ne s'installe qu'à la
 * fin, et seulement si le pipeline IA a abouti. Rien n'est écrit dans le plan
 * ni en base avant la validation d'un groupe.
 */
/**
 * @param diagnosticId ouvrage lié au plan — requis seulement pour valider
 * (le bouton Analyser peut s'en passer).
 */
export function usePlanAnalysis(diagnosticId: Ref<number> = ref(0)): {
  runAnalysis: () => Promise<boolean>;
  validateGroup: (
    group: AnalysisGroup,
    options: {
      partId?: number;
      name?: string;
      description?: string;
      kind?: AnalysisGroupKind;
    },
  ) => Promise<boolean>;
  ignoreGroup: (group: AnalysisGroup) => void;
  restoreGroup: (group: AnalysisGroup) => void;
  locateGroup: (group: AnalysisGroup) => void;
  correctGroup: (group: AnalysisGroup, mode: "add" | "remove") => number;
  getGroupEntities: (group: AnalysisGroup) => AnalysisEntity[];
  focusEntity: (handle: string) => void;
  removeEntityFromGroup: (group: AnalysisGroup, handle: string) => void;
  /** Nouveau groupe (manuel) depuis la sélection du plan ; nb d'entités prises, 0 si rien d'analysable. */
  createGroupFromSelection: () => number;
  moveEntityToGroup: (
    group: AnalysisGroup,
    handle: string,
    targetGroupId: string,
  ) => void;
  regenerateSuggestion: (group: AnalysisGroup) => Promise<boolean>;
  regeneratingGroupId: Ref<string | null>;
  exportFixture: () => void;
} {
  const state = usePlanAnalysisState();
  const { t } = useTranslation();

  /**
   * Arrondi des coordonnées normalisées envoyées à l'IA. 4 décimales : à 3,
   * un plan dont les annotations n'occupent que quelques % de la feuille
   * voyait des désordres distincts confondus en un même point (vécu).
   */
  const round4 = (v: number): number => Math.round(v * 10000) / 10000;

  /**
   * Une analyse rechargée de Dexie porte les handles d'une session passée —
   * seuls les `entityUid` sont stables à travers les sauvegardes. Avant toute
   * sélection/validation, on réhydrate une fois les handles depuis le plan
   * ouvert, puis on relit le groupe dans le store (l'objet capté par
   * l'appelant est figé d'avant la réhydratation).
   */
  const ensureHandlesFresh = (): void => {
    if (state.handlesRehydrated || !state.hasAnalysis) return;
    if (!Facade.isInitialized) return;
    state.rehydrateHandles(Facade.observations.mapAnalysisEntityHandles());
  };

  const freshGroup = (group: AnalysisGroup): AnalysisGroup => {
    ensureHandlesFresh();
    return state.groups.find((g) => g.id === group.id) ?? group;
  };

  const { mutateAsync: createObservation } = useCreateFieldObservation();

  /**
   * Scan du plan avec la tolérance aux stamps orphelins de la dernière
   * analyse : une entité rattachée à une observation disparue (supprimée
   * depuis le bureau, un autre appareil…) reste candidate. Sans contexte
   * connu, toute entité estampée est exclue — comportement prudent.
   */
  const scanCandidates = (): AnalysisScanResult =>
    Facade.observations.scanAnalysisCandidates({
      ...(state.liveObservationIds
        ? { liveObservationIds: new Set(state.liveObservationIds) }
        : {}),
    });

  /**
   * `localId` des observations vivantes de l'ouvrage lié au plan — null si
   * le plan n'est pas lié ou si le serveur est injoignable (on garde alors
   * le dernier contexte connu, à défaut l'exclusion prudente).
   */
  const fetchLiveObservationIds = async (): Promise<string[] | null> => {
    const drawingId = state.drawingId;
    if (!drawingId) return null;
    try {
      let drawing = await drawingRepository.getById(drawingId);
      const [fresh] = await APIClient.fetchDrawings({
        ids: [drawingId],
        archived: false,
        skip: 0,
        take: 1,
        sortBy: "createdAt",
        order: "desc",
      });
      if (fresh) drawing = fresh;
      const linkedDiagnosticId = drawing?.diagnostic?.id;
      if (!linkedDiagnosticId) return null;
      const observations = await APIClient.getFieldObservations(linkedDiagnosticId);
      return observations
        .map((o) => o.localId)
        .filter((id): id is string => !!id);
    } catch (e) {
      Logger.warn(`usePlanAnalysis : live observations unavailable (${e})`);
      return null;
    }
  };

  /** Centre d'un groupe normalisé 0..1 dans une emprise donnée. */
  const normalizedPositionIn = (
    plan: WorldBbox | null,
    group: AnalysisGroup,
  ): { x: number; y: number } | null => {
    if (!plan) return null;
    const width = plan.maxX - plan.minX;
    const height = plan.maxY - plan.minY;
    if (width <= 0 || height <= 0) return null;
    const cx = (group.bbox.minX + group.bbox.maxX) / 2;
    const cy = (group.bbox.minY + group.bbox.maxY) / 2;
    return {
      x: round4((cx - plan.minX) / width),
      y: round4((cy - plan.minY) / height),
    };
  };

  /** Origine des temps relatifs envoyés à l'IA : première entité horodatée. */
  const firstTimestamp = (entities: AnalysisEntity[]): number => {
    const times = entities
      .map((e) => e.createdAt)
      .filter((t): t is number => t !== null);
    return times.length ? Math.min(...times) : 0;
  };

  /**
   * Entités candidates pour l'IA — SANS les photos : elles sont rattachées
   * par la passe photos et décrites à part (`buildPhotosForAI`). Positions
   * normalisées, secondes relatives à la première entité horodatée.
   */
  const buildEntitiesForAI = (
    entities: AnalysisEntity[],
    plan: WorldBbox | null,
    planDiagonal: number,
    vignettes: PlanVignette[],
    t0: number,
  ): PlanEntityForAI[] => {
    const width = plan ? plan.maxX - plan.minX : 0;
    const height = plan ? plan.maxY - plan.minY : 0;
    const diagonal = planDiagonal || 1;
    const normalize = (x: number, y: number): { x: number; y: number } =>
      plan && width > 0 && height > 0
        ? { x: round4((x - plan.minX) / width), y: round4((y - plan.minY) / height) }
        : { x: 0, y: 0 };

    return entities
      .filter((e) => e.bbox !== null && classifyEntity(e) !== "marker")
      .map((e) => {
        const bbox = e.bbox as NonNullable<AnalysisEntity["bbox"]>;
        const text = e.text?.trim();
        return {
          id: e.handle,
          role: classifyEntity(e),
          kind: e.kind,
          defectName: e.defectName,
          center: normalize((bbox.minX + bbox.maxX) / 2, (bbox.minY + bbox.maxY) / 2),
          size: round4(bboxDiagonal(bbox) / diagonal),
          t: e.createdAt !== null ? Math.round((e.createdAt - t0) / 1000) : null,
          ...(text ? { text } : {}),
          ...(e.leaderTips?.length
            ? { leaderTips: e.leaderTips.map((tip) => normalize(tip.x, tip.y)) }
            : {}),
          ...(e.length !== null ? { length: Math.round(e.length * 100) / 100 } : {}),
          ...(e.area !== null ? { area: Math.round(e.area * 100) / 100 } : {}),
          ...(vignettes.length
            ? { vignetteId: vignetteIdForEntity(e, vignettes, planDiagonal) }
            : {}),
        };
      });
  };

  /** Vignettes pour l'IA : bbox normalisées 0..1 dans l'emprise de la feuille. */
  const buildViewsForAI = (
    vignettes: PlanVignette[],
    plan: WorldBbox | null,
  ): PlanGroupingRequest["views"] => {
    if (!plan || vignettes.length === 0) return undefined;
    const width = plan.maxX - plan.minX;
    const height = plan.maxY - plan.minY;
    if (width <= 0 || height <= 0) return undefined;
    return vignettes.map((v) => ({
      id: v.id,
      ...(v.name ? { name: v.name } : {}),
      bbox: {
        minX: round4((v.bbox.minX - plan.minX) / width),
        minY: round4((v.bbox.minY - plan.minY) / height),
        maxX: round4((v.bbox.maxX - plan.minX) / width),
        maxY: round4((v.bbox.maxY - plan.minY) / height),
      },
    }));
  };

  /**
   * Faits de la passe photos pour l'IA : chaque photo, le groupe proposé
   * retenu, la règle qui a tranché et ses candidats mesurés (distance, visée,
   * enveloppe, écart temporel en secondes, photos déjà rattachées).
   */
  const buildPhotosForAI = (
    local: PlanAnalysisResult,
    proposalIdOf: Map<string, string>,
    byHandle: Map<string, AnalysisEntity>,
    t0: number,
  ): PlanPhotoFactsForAI[] => {
    const seconds = (ms: number | null): number | null =>
      ms === null ? null : Math.round(ms / 1000);
    const photos: PlanPhotoFactsForAI[] = [];
    for (const group of local.groups) {
      const ownId = proposalIdOf.get(group.id) ?? null;
      for (const photo of group.photoAttachments ?? []) {
        const entity = byHandle.get(photo.handle);
        const attached = photo.rule !== "standalone" && ownId !== null;
        photos.push({
          id: photo.handle,
          markerNumber: photo.markerNumber,
          markerShape: entity?.markerShape ?? null,
          t:
            entity?.createdAt != null
              ? Math.round((entity.createdAt - t0) / 1000)
              : null,
          groupId: attached ? ownId : null,
          rule: photo.rule,
          candidates: [
            ...(attached
              ? [
                  {
                    groupId: ownId as string,
                    distance: round4(photo.distance ?? 0),
                    pointed: photo.pointed,
                    inside: photo.inside,
                    deltaT: seconds(photo.deltaT),
                    photoCount: Math.max(0, group.markerHandles.length - 1),
                  },
                ]
              : []),
            ...photo.alternatives
              .filter((a) => proposalIdOf.has(a.groupId))
              .map((a) => ({
                groupId: proposalIdOf.get(a.groupId) as string,
                distance: round4(a.distance),
                pointed: a.pointed,
                inside: a.inside,
                deltaT: seconds(a.deltaT),
                photoCount: a.photoCount,
              })),
          ],
        });
      }
    }
    return photos;
  };

  const buildNotesForAI = (): PlanGroupingRequest["notes"] => {
    const { noteInfo } = buildLegendNoteInfo();
    return [...noteInfo.entries()].map(([name, info]) => ({
      name,
      ...(info.label ? { label: info.label } : {}),
      ...(info.category ? { category: info.category } : {}),
    }));
  };

  /**
   * Le rattachement aux parties par l'IA est une fonctionnalité FUTURE : la
   * proposition `partId` du describe est écartée avant d'installer la
   * suggestion (le choix de partie reste à l'inspecteur).
   */
  const withoutPart = (
    suggestions: Record<string, PlanAnalysisAiSuggestion>,
  ): Record<string, PlanAnalysisAiSuggestion> =>
    Object.fromEntries(
      Object.entries(suggestions).map(([id, s]) => {
        const { partId: _p, partName: _n, ...rest } = s;
        return [id, rest];
      }),
    );

  /** Payload describe (passe de rédaction, et régénération à la demande). */
  const describePayloadFor = (
    groups: AnalysisGroup[],
    plan: WorldBbox | null,
  ): PlanAnalysisAiRequest => ({
    groups: groups.map((g) => ({
      id: g.id,
      defectName: g.defectName,
      defectLabel: g.defectLabel,
      category: g.category,
      tracedCount: g.tracedCount,
      context: g.context,
      timeRange: g.timeRange,
      confidence: g.confidence,
      position: normalizedPositionIn(plan, g),
    })),
    notes: buildNotesForAI(),
    ...(state.unit ? { unit: state.unit } : {}),
  });

  /**
   * Transforme le découpage tranché par l'IA en groupes assemblés. L'IA ne
   * partitionne que les entités NON-photo : celles qu'elle n'a pas couvertes
   * retombent dans leur groupe local ; puis chaque photo rejoint le groupe
   * qui a hérité de SA cible — celle de l'algorithme, ou le candidat choisi
   * par l'IA pour une photo ambiguë, jamais autre chose. Les photos
   * autonomes restent des vues d'ensemble. Renvoie la liste triée.
   */
  const partitionFromAi = (
    ai: PlanGroupingResponse,
    scan: AnalysisScanResult,
    planDiagonal: number,
    localResult: PlanAnalysisResult,
    localIdOfProposal: Map<string, string>,
  ): { groups: AnalysisGroup[]; covered: Set<string> } => {
    const byHandle = new Map(scan.entities.map((e) => [e.handle, e]));
    const { noteInfo } = buildLegendNoteInfo();
    const isMarker = (e: AnalysisEntity): boolean =>
      classifyEntity(e) === "marker";

    interface Draft {
      members: AnalysisEntity[];
      kind?: AnalysisGroupKind;
      reasoning?: string;
      photos: PhotoAttachment[];
    }
    const drafts: Draft[] = [];
    const covered = new Set<string>();
    for (const aiGroup of ai.groups) {
      const members = aiGroup.entityIds
        .filter((id) => !covered.has(id))
        .map((id) => byHandle.get(id))
        .filter((e): e is AnalysisEntity => !!e && !isMarker(e));
      if (members.length === 0) continue;
      members.forEach((m) => covered.add(m.handle));
      // La nature n'est choisie par l'IA que pour un groupe de textes seuls.
      const textOnly = members.every((m) => classifyEntity(m) === "annotation");
      drafts.push({
        members,
        ...(textOnly && aiGroup.kind !== "overview" ? { kind: aiGroup.kind } : {}),
        ...(aiGroup.reasoning ? { reasoning: aiGroup.reasoning } : {}),
        photos: [],
      });
    }
    // Reliquats : les entités non couvertes gardent leur découpage local.
    for (const group of localResult.groups) {
      const leftover = group.handles
        .filter((h) => !covered.has(h))
        .map((h) => byHandle.get(h))
        .filter((e): e is AnalysisEntity => !!e && !isMarker(e));
      if (leftover.length === 0) continue;
      drafts.push({ members: leftover, photos: [] });
    }

    // Le brouillon qui a hérité d'un groupe local = celui qui partage le
    // plus d'entités avec lui.
    const draftFor = (localGroupId: string): Draft | null => {
      const local = localResult.groups.find((g) => g.id === localGroupId);
      if (!local) return null;
      const wanted = new Set(local.handles);
      let best: Draft | null = null;
      let bestCount = 0;
      for (const draft of drafts) {
        const count = draft.members.filter((m) => wanted.has(m.handle)).length;
        if (count > bestCount) {
          best = draft;
          bestCount = count;
        }
      }
      return best;
    };
    const choiceOf = new Map(
      (ai.photoChoices ?? []).map((c) => [
        c.photoId,
        localIdOfProposal.get(c.groupId) ?? null,
      ]),
    );

    const standalone: Draft[] = [];
    for (const group of localResult.groups) {
      const attachments = new Map(
        (group.photoAttachments ?? []).map((p) => [p.handle, p]),
      );
      for (const handle of group.markerHandles) {
        const marker = byHandle.get(handle);
        if (!marker) continue;
        const attachment: PhotoAttachment = attachments.get(handle) ?? {
          handle,
          markerNumber: marker.markerNumber,
          rule: "standalone",
          distance: null,
          pointed: false,
          inside: false,
          deltaT: null,
          alternatives: [],
        };
        const choice = choiceOf.get(handle) ?? null;
        const targetLocalId =
          choice ?? (attachment.rule === "standalone" ? null : group.id);
        const draft = targetLocalId ? draftFor(targetLocalId) : null;
        if (!draft) {
          // Photo autonome (ou cible disparue) : vue d'ensemble à part.
          standalone.push({
            members: [marker],
            kind: "overview",
            photos: [attachment],
          });
          continue;
        }
        draft.members.push(marker);
        draft.photos.push(choice ? { ...attachment, rule: "ai" } : attachment);
        covered.add(handle);
      }
    }

    // Ids connus avant composition (empreinte des membres) : les alternatives
    // d'une photo désignent les groupes FINAUX.
    const all = [...drafts, ...standalone];
    const idOf = new Map(
      all.map((d) => [d, stableGroupId(d.members.map((m) => m.entityUid))]),
    );
    const finalIdOfLocal = (localId: string): string | null => {
      const draft = draftFor(localId);
      return draft ? (idOf.get(draft) ?? null) : null;
    };
    const groups: AnalysisGroup[] = [];
    for (const draft of all) {
      const ownId = idOf.get(draft);
      const photoAttachments = draft.photos.map((attachment) => ({
        ...attachment,
        alternatives: attachment.alternatives
          .map((a): PhotoCandidateFacts | null => {
            const id = finalIdOfLocal(a.groupId);
            return id && id !== ownId ? { ...a, groupId: id } : null;
          })
          .filter((a): a is PhotoCandidateFacts => a !== null),
      }));
      const group = composeGroup(draft.members, planDiagonal, noteInfo, {
        ...(draft.kind ? { kind: draft.kind } : {}),
        photoAttachments,
      });
      if (!group) continue;
      groups.push(
        draft.reasoning ? { ...group, aiReasoning: draft.reasoning } : group,
      );
    }
    return { groups: sortAnalysisGroups(groups), covered };
  };

  /**
   * Pipeline IA complet, AVANT affichage : grouping (le découpage) puis
   * describe (les textes). Renvoie null en cas d'échec — l'appelant retombe
   * alors sur le résultat local.
   */
  const runAiPipeline = async (
    drawingId: number,
    scan: AnalysisScanResult,
    planDiagonal: number,
    planBbox: WorldBbox | null,
    vignettes: PlanVignette[],
    localResult: PlanAnalysisResult,
  ): Promise<{
    result: PlanAnalysisResult;
    suggestions: Record<string, PlanAnalysisAiSuggestion>;
  } | null> => {
    try {
      // ── Passe 1 : l'IA arbitre le découpage ─────────────────────────────
      await showStep("ai_grouping");
      const byHandle = new Map(scan.entities.map((e) => [e.handle, e]));
      const t0 = firstTimestamp(scan.entities);
      const views = buildViewsForAI(vignettes, planBbox);
      // Proposition : les groupes locaux ayant au moins une entité non-photo,
      // sous des ids courts — la référence des candidats photo.
      const proposalIdOf = new Map<string, string>();
      const localIdOfProposal = new Map<string, string>();
      const proposal: PlanGroupingRequest["proposal"] = [];
      for (const g of localResult.groups) {
        const entityIds = g.handles.filter((h) => {
          const e = byHandle.get(h);
          return !!e && classifyEntity(e) !== "marker";
        });
        if (entityIds.length === 0) continue;
        const id = `P${proposal.length + 1}`;
        proposalIdOf.set(g.id, id);
        localIdOfProposal.set(id, g.id);
        proposal.push({ id, entityIds, kind: g.kind });
      }
      const grouping = await APIClient.analyzePlanGrouping(drawingId, {
        entities: buildEntitiesForAI(
          scan.entities,
          planBbox,
          planDiagonal,
          vignettes,
          t0,
        ),
        proposal,
        doubts: buildAnalysisDoubts(localResult, scan.entities, planDiagonal),
        notes: buildNotesForAI(),
        ...(views ? { views } : {}),
        photos: buildPhotosForAI(localResult, proposalIdOf, byHandle, t0),
      });
      if (grouping.groups.length === 0) return null;

      const { groups, covered } = partitionFromAi(
        grouping,
        scan,
        planDiagonal,
        localResult,
        localIdOfProposal,
      );
      const result: PlanAnalysisResult = {
        groups,
        // Un orphelin que l'IA a su rattacher n'est plus orphelin.
        unassignedTextHandles: localResult.unassignedTextHandles.filter(
          (h) => !covered.has(h),
        ),
        unassignedMarkerHandles: localResult.unassignedMarkerHandles.filter(
          (h) => !covered.has(h),
        ),
      };

      // ── Passe 2 : l'IA rédige les suggestions (désordres et notes) ──────
      let suggestions: Record<string, PlanAnalysisAiSuggestion> = {};
      const disorders = groups.filter((g) => g.kind !== "overview");
      if (disorders.length > 0) {
        await showStep(
          "ai_describe",
          t("canvas:analysis.steps.detail.aiDescribe", {
            count: disorders.length,
          }),
        );
        const described = await APIClient.describePlanObservations(
          drawingId,
          describePayloadFor(disorders, planBbox),
        );
        const known = new Set(groups.map((g) => g.id));
        suggestions = withoutPart(
          Object.fromEntries(
            Object.entries(described.suggestions).filter(([id]) => known.has(id)),
          ),
        );
      }
      return { result, suggestions };
    } catch (e) {
      // Hors ligne ou serveur en erreur : le résultat local servira.
      Logger.warn(`usePlanAnalysis : AI pipeline failed (${e})`);
      return null;
    }
  };

  /**
   * Détail des entités d'un groupe (pour la liste dépliable de la carte) —
   * relues du plan à la demande, dans l'ordre du groupe.
   */
  const getGroupEntities = (group: AnalysisGroup): AnalysisEntity[] => {
    if (!Facade.isInitialized) return [];
    const handles = freshGroup(group).handles;
    const scan = scanCandidates();
    const byHandle = new Map(scan.entities.map((e) => [e.handle, e]));
    return handles
      .map((h) => byHandle.get(h))
      .filter((e): e is AnalysisEntity => !!e);
  };

  /**
   * Sélectionne UNE entité du groupe, sans re-zoomer : le groupe est déjà à
   * l'écran (son cadre est focus), re-cadrer désorienterait.
   */
  const focusEntity = (handle: string): void => {
    if (!Facade.isInitialized) return;
    Facade.selection.selectByHandles([handle]);
  };

  /**
   * Recompose un groupe corrigé à la main : nature DÉRIVÉE des membres
   * restants, rattachements photo conservés pour les marqueurs encore là
   * (une photo déplacée par l'inspecteur n'a plus d'alternative à proposer).
   */
  const recompose = (group: AnalysisGroup, members: AnalysisEntity[]): void => {
    if (members.length === 0) {
      state.removeGroup(group.id);
      return;
    }
    const { noteInfo } = buildLegendNoteInfo();
    const alive = new Set(members.map((m) => m.handle));
    const rebuilt = composeGroup(members, state.planDiagonal, noteInfo, {
      photoAttachments: (group.photoAttachments ?? []).filter((p) =>
        alive.has(p.handle),
      ),
    });
    if (rebuilt) state.replaceGroup(group.id, rebuilt);
    else state.removeGroup(group.id);
  };

  /** Retire une entité du groupe (recomposé : mesures, confiance suivent). */
  const removeEntityFromGroup = (group: AnalysisGroup, handle: string): void => {
    if (!Facade.isInitialized) return;
    const scan = scanCandidates();
    const byHandle = new Map(scan.entities.map((e) => [e.handle, e]));
    const members = freshGroup(group)
      .handles.filter((h) => h !== handle)
      .map((h) => byHandle.get(h))
      .filter((e): e is AnalysisEntity => !!e);
    recompose(group, members);
    trackTab("analysis", "group_corrected", { mode: "entity_removed" });
  };

  /**
   * Crée un groupe À LA MAIN depuis la sélection courante du plan : les
   * entités analysables sélectionnées quittent leur groupe actuel (recomposé,
   * ou retiré s'il se vide) et forment un nouveau groupe, marqué `manual`,
   * ajouté en fin de liste et rendu courant pour être complété tout de suite.
   */
  const createGroupFromSelection = (): number => {
    if (!Facade.isInitialized) return 0;
    const selection = new Set(Facade.selection.handles);
    if (selection.size === 0) return 0;
    const scan = scanCandidates();
    const members = scan.entities.filter((e) => selection.has(e.handle));
    if (members.length === 0) return 0;
    ensureHandlesFresh();
    const byHandle = new Map(scan.entities.map((e) => [e.handle, e]));
    const taken = new Set(members.map((m) => m.handle));
    // D'abord sortir les entités de leurs groupes actuels.
    for (const group of [...state.groups]) {
      if (!group.handles.some((h) => taken.has(h))) continue;
      const remaining = group.handles
        .filter((h) => !taken.has(h))
        .map((h) => byHandle.get(h))
        .filter((e): e is AnalysisEntity => !!e);
      recompose(group, remaining);
    }
    const { noteInfo } = buildLegendNoteInfo();
    const group = composeGroup(members, state.planDiagonal, noteInfo, {
      manual: true,
    });
    if (!group) return 0;
    state.addGroup(group);
    Facade.selection.clear();
    trackTab("analysis", "group_corrected", {
      mode: "created",
      count: members.length,
    });
    return members.length;
  };

  /**
   * Déplace une entité (typiquement une photo « à confirmer ») vers un autre
   * groupe : les deux sont recomposés. La source d'abord — son id change —
   * puis la cible, relue par son id inchangé.
   */
  const moveEntityToGroup = (
    group: AnalysisGroup,
    handle: string,
    targetGroupId: string,
  ): void => {
    if (!Facade.isInitialized || targetGroupId === group.id) return;
    const target = state.groups.find((g) => g.id === targetGroupId);
    const scan = scanCandidates();
    const byHandle = new Map(scan.entities.map((e) => [e.handle, e]));
    const entity = byHandle.get(handle);
    if (!target || !entity) return;
    const sourceMembers = freshGroup(group)
      .handles.filter((h) => h !== handle)
      .map((h) => byHandle.get(h))
      .filter((e): e is AnalysisEntity => !!e);
    recompose(group, sourceMembers);
    const targetMembers = freshGroup(target)
      .handles.map((h) => byHandle.get(h))
      .filter((e): e is AnalysisEntity => !!e && e.handle !== handle);
    recompose(target, [...targetMembers, entity]);
    trackTab("analysis", "group_corrected", { mode: "entity_moved" });
  };

  // ── Régénération de la suggestion d'UN groupe (après correction) ──────────
  const regeneratingGroupId = ref<string | null>(null);

  const regenerateSuggestion = async (
    group: AnalysisGroup,
  ): Promise<boolean> => {
    const drawingId = state.drawingId;
    if (!drawingId || regeneratingGroupId.value) return false;
    regeneratingGroupId.value = group.id;
    try {
      const result = await APIClient.describePlanObservations(
        drawingId,
        describePayloadFor([group], state.planBbox),
      );
      const suggestion = withoutPart(result.suggestions)[group.id];
      if (suggestion) state.mergeAiSuggestions({ [group.id]: suggestion });
      trackTab("analysis", "suggestion_regenerated");
      return !!suggestion;
    } catch (e) {
      Logger.warn(`usePlanAnalysis : regenerate failed (${e})`);
      return false;
    } finally {
      regeneratingGroupId.value = null;
    }
  };

  /** Durée minimale d'affichage d'une étape du pipeline (lisible, pas un flash). */
  const STEP_MIN_MS = 350;

  /** Affiche l'étape (et son détail) sur le plan, puis laisse le temps de la lire. */
  const showStep = async (step: AnalysisStep, detail?: string): Promise<void> => {
    state.setAnalysisStep(step, detail);
    await new Promise((resolve) => setTimeout(resolve, STEP_MIN_MS));
  };

  /** Photos rattachées / à confirmer dans un résultat (détail d'étape). */
  const photoStats = (
    result: PlanAnalysisResult,
  ): { count: number; uncertain: number } => {
    let count = 0;
    let uncertain = 0;
    for (const group of result.groups) {
      for (const photo of group.photoAttachments ?? []) {
        count++;
        if (photo.rule === "ambiguous" || photo.rule === "temporal") uncertain++;
      }
    }
    return { count, uncertain };
  };

  /**
   * Analyse complète : scan (une traversée) → détection des vignettes →
   * regroupement local + passe photos (le prior) → pipeline IA (grouping +
   * describe) — chaque étape s'affiche sur le plan pendant le balayage, le
   * résultat ne s'installe qu'à la fin.
   *
   * L'analyse est un outil de bureau, en ligne : si le pipeline IA échoue,
   * RIEN n'est installé (l'analyse précédente reste intacte) et l'échec est
   * signalé — pas de repli silencieux sur le découpage local. Relancer
   * remplace les groupes en attente et conserve les validés/ignorés
   * (cf. `setAnalysisResult`).
   */
  const runAnalysis = async (): Promise<boolean> => {
    if (state.isAnalyzing) return false;
    // L'unité du plan se choisit AVANT le lancement (cf. `useAnalysisLaunch`) :
    // l'IA rédige les mesures avec elle, la corriger après coup arrive trop tard.
    // Lancement impossible (moteur de dessin pas prêt, unité absente) : journalisé
    // seulement, l'outil dessin n'affiche aucun toast.
    if (!Facade.isInitialized || !state.unit) {
      Logger.warn(
        `usePlanAnalysis : analysis not started (engine ready: ${Facade.isInitialized}, unit: ${state.unit ?? "undefined"})`,
      );
      return false;
    }
    state.isAnalyzing = true;
    trackTab("analysis", "analysis_started");
    try {
      await showStep("scan");
      const liveIds = await fetchLiveObservationIds();
      if (liveIds) state.liveObservationIds = liveIds;
      const scan = scanCandidates();
      state.setScanContext(state.liveObservationIds, scan.excluded);
      const planDiagonal = scan.planBbox ? bboxDiagonal(scan.planBbox) : 0;
      if (planDiagonal === 0 || scan.entities.length === 0) {
        state.setAnalysisResult(
          {
            groups: [],
            unassignedTextHandles: [],
            unassignedMarkerHandles: [],
          },
          planDiagonal,
          scan.planBbox,
        );
        return true;
      }
      await showStep(
        "scan",
        t("canvas:analysis.steps.detail.scan", { count: scan.entities.length }),
      );
      await showStep("views");
      const vignettes = detectVignettes(scan.backgroundItems, scan.planBbox);
      await showStep(
        "views",
        t("canvas:analysis.steps.detail.views", { count: vignettes.length }),
      );
      await showStep("grouping");
      const { noteInfo } = buildLegendNoteInfo();
      const localResult = groupEntities({
        entities: scan.entities,
        planDiagonal,
        noteInfo,
        vignettes,
      });
      await showStep(
        "grouping",
        t("canvas:analysis.steps.detail.grouping", {
          count: localResult.groups.length,
        }),
      );
      const photos = photoStats(localResult);
      await showStep(
        "photos",
        t("canvas:analysis.steps.detail.photos", photos),
      );

      state.isAiRunning = true;
      let ai: Awaited<ReturnType<typeof runAiPipeline>> = null;
      try {
        if (state.drawingId) {
          ai = await runAiPipeline(
            state.drawingId,
            scan,
            planDiagonal,
            scan.planBbox,
            vignettes,
            localResult,
          );
        }
      } finally {
        state.isAiRunning = false;
      }

      if (!ai) {
        Logger.warn("usePlanAnalysis : AI pass failed");
        return false;
      }

      state.setAnalysisResult(ai.result, planDiagonal, scan.planBbox, vignettes);
      state.setAiSuggestions(ai.suggestions);
      trackTab("analysis", "ai_pass_done");
      return true;
    } finally {
      state.isAnalyzing = false;
      state.setAnalysisStep(null);
    }
  };

  /**
   * Export dev d'une fixture de calibration : la sortie brute du scan + le
   * découpage COURANT comme vérité terrain (corriger les groupes dans l'app
   * avant d'exporter). À déposer dans
   * `src/utils/plan-analysis/__fixtures__/` — cf. `benchmark.ts`.
   */
  const exportFixture = (): void => {
    if (!Facade.isInitialized) return;
    const scan = scanCandidates();
    const planDiagonal = scan.planBbox ? bboxDiagonal(scan.planBbox) : 0;
    const name = `drawing-${state.drawingId ?? "unknown"}-${new Date()
      .toISOString()
      .slice(0, 10)}`;
    const fixture = {
      name,
      input: {
        entities: scan.entities,
        planDiagonal,
        planBbox: scan.planBbox,
        backgroundItems: scan.backgroundItems,
        notes: buildNotesForAI(),
      },
      expected: state.groups.map((g) => [...g.entityUids]),
      minF1: 0,
      minPhotoAccuracy: 0,
    };
    const blob = new Blob([JSON.stringify(fixture, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${name}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  /**
   * Valide un groupe : crée la FieldObservation (`localId` systématique, clé
   * du rattachement), estampe `observationId` sur TOUTES les entités du
   * groupe (tracés + textes + marqueurs — le rattachement photo passe par les
   * numéros de marqueurs), puis marque le groupe validé.
   */
  const validateGroup = async (
    group: AnalysisGroup,
    options: {
      partId?: number;
      name?: string;
      description?: string;
      kind?: AnalysisGroupKind;
    },
  ): Promise<boolean> => {
    if (!diagnosticId.value || !Facade.isInitialized) return false;
    const localId = uuid();
    // Type d'observation : le choix de l'inspecteur, sinon la nature dérivée
    // du groupe (désordre / vue d'ensemble / commentaire).
    const kind = options.kind ?? group.kind;
    await createObservation({
      type:
        kind === "overview"
          ? "OVERVIEW"
          : kind === "comment"
            ? "COMMENT"
            : "DISORDER",
      diagnosticId: diagnosticId.value,
      ...(options.partId ? { partId: options.partId } : {}),
      ...(options.name?.trim() ? { name: options.name.trim() } : {}),
      ...(options.description?.trim()
        ? { description: options.description.trim() }
        : {}),
      localId,
    });
    const stamped = Facade.observations.setEntities(
      freshGroup(group).handles,
      localId,
    );
    Logger.info(
      `usePlanAnalysis : group ${group.id} validated as observation ${localId} (${stamped} entities stamped)`,
    );
    state.validateGroup(group.id, localId);
    trackTab("analysis", "group_validated");
    return true;
  };

  const ignoreGroup = (group: AnalysisGroup): void => {
    state.ignoreGroup(group.id);
    trackTab("analysis", "group_ignored");
  };

  const restoreGroup = (group: AnalysisGroup): void => {
    state.restoreGroup(group.id);
  };

  /** Rend le groupe courant et zoome dessus (sélection + cadre redessiné). */
  const locateGroup = (group: AnalysisGroup): void => {
    state.setCurrentGroup(group.id);
    if (!Facade.isInitialized) return;
    const selected = Facade.selection.highlightByHandles(freshGroup(group).handles);
    if (selected === 0) {
      Logger.warn(
        `usePlanAnalysis : locate found no entity for group ${group.id} (stale analysis?)`,
      );
    }
  };

  /**
   * Corrige un groupe avec la sélection courante (ajout ou retrait), puis le
   * recompose depuis un rescan — mesures, désordre dominant et confiance
   * suivent. Les entités non candidates (fond de plan, déjà rattachées) sont
   * ignorées silencieusement.
   * @returns le nombre d'entités effectivement ajoutées/retirées.
   */
  const correctGroup = (
    group: AnalysisGroup,
    mode: "add" | "remove",
  ): number => {
    if (!Facade.isInitialized) return 0;
    const selection = Facade.selection.handles;
    if (selection.length === 0) return 0;

    const scan = scanCandidates();
    const byHandle = new Map(scan.entities.map((e) => [e.handle, e]));
    const current = new Set(freshGroup(group).handles);
    let changed = 0;
    if (mode === "add") {
      for (const h of selection) {
        if (byHandle.has(h) && !current.has(h)) {
          current.add(h);
          changed++;
        }
      }
    } else {
      for (const h of selection) {
        if (current.delete(h)) changed++;
      }
    }
    if (changed === 0) return 0;

    const members = [...current]
      .map((h) => byHandle.get(h))
      .filter((e): e is AnalysisEntity => !!e);
    recompose(group, members);
    trackTab("analysis", "group_corrected", { mode });
    return changed;
  };

  return {
    runAnalysis,
    validateGroup,
    ignoreGroup,
    restoreGroup,
    locateGroup,
    correctGroup,
    getGroupEntities,
    focusEntity,
    removeEntityFromGroup,
    createGroupFromSelection,
    moveEntityToGroup,
    regenerateSuggestion,
    regeneratingGroupId,
    exportFixture,
  };
}
